import { Component, Prop, Vue } from "vue-property-decorator";
import http from "@/services/Http";
import * as Models from "./models";
import { productConfiguratorStore as store } from "./store";
import Cookie from "@/services/Cookie";

// Modules
import RadioButtonInputModule from "./components/RadioButtonInputModule";
import ImageInputModule from "./components/ImageInputModule";
import TextInputModule from "./components/TextInputModule";
import ContentModule from "./components/ContentModule";
import ProductListModule from "./components/ProductListModule";
import ProductSummaryModule from "./components/ProductSummaryModule";
import SelectedOptionsSummaryModule from "./components/SelectedOptionsSummaryModule";
import StraightWallSectionModule from "./components/StraightWallSectionModule";
import CurvedWallSectionModule from "./components/CurvedWallSectionModule";
import BuildingOutdoorWallSectionModule from "./components/BuildingOutdoorWallSectionModule";
import BuildingIndoorWallSectionModule from "./components/BuildingIndoorWallSectionModule";
import BathroomWallSectionModule from "./components/BathroomWallSectionModule";
import ProductListCustomModule from "./components/ProductListCustomModule";
import StepByStepInstructionsProductListModule from "./components/StepByStepInstructionsProductListModule";
import { EventBus } from "../../events";
import { configuratorConstants } from "./models";
import { CarbonSavingsModel } from "@/models/product";

@Component({
    components: {
        RadioButtonInputModule,
        ImageInputModule,
        TextInputModule,
        ContentModule,
        ProductListModule,
        ProductSummaryModule,
        SelectedOptionsSummaryModule,
        StraightWallSectionModule,
        CurvedWallSectionModule,
        BuildingOutdoorWallSectionModule,
        BuildingIndoorWallSectionModule,
        BathroomWallSectionModule,
        ProductListCustomModule,
        StepByStepInstructionsProductListModule,
  }
})
export default class ProductConfigurator extends Vue {
    protected vm: Models.ProductConfiguratorModel  = { } as Models.ProductConfiguratorModel;
    protected activeStepIndex: number = 0;
    protected translationModel: Models.TranslationModel = {} as Models.TranslationModel;
    protected displayQuantityChangedMessage: boolean = false;
    protected availableProducts: Models.Product[] = [];
    protected addedProductsInSession: Models.Product[] = [];
    protected selectedConfiguratorProductCookie: string = "";
    protected selectedCustomConfiguratorProductCookie: string = "";
    protected isMandatoryStepPropertyValuesSet: boolean = false;
    protected sharedConfiguratorInfoFound: boolean = false;
    private apiUrl: string = store.apiUrl;
    private readonly quantityChangedModalName: string = "info-message--quantity";
    private selectedConfiguratorProducts: string = "SelectedConfiguratorProducts-";
    private selectedCustomConfiguratorProducts: string = "SelectedCustomConfiguratorProducts-";

    protected checkMandatoryStepPropertyValuesSet() {
        if (this.cookieExists()) {
            let cookieStringList = Cookie.getCookieStringList(this.selectedConfiguratorProducts);
            const cookieCustomStringList = Cookie.getCookieStringList(this.selectedCustomConfiguratorProducts);
            cookieStringList = cookieStringList.concat(cookieCustomStringList);
            this.isMandatoryStepPropertyValuesSet = this.isConfigIncluded(cookieStringList);
        }
        const valueDefinedProperties = store.configuratorValues.filter(x => x.value && x.valueDescriptions).map(x => x.property.propertyName);
        this.isMandatoryStepPropertyValuesSet = this.isConfigIncluded(valueDefinedProperties);
    }

    protected isConfigIncluded(configList: any): boolean {
        let containsGroundShapeOrCorners = false;
        return this.vm.steps[this.activeStepIndex]
        .sections.every(section =>
                section.modules.every(module => !module.configuratorProperties || module.configuratorProperties.filter(x => x.mandatory).every(prop => {
                    const propValue = store.configuratorValues.filter(x => x.property.propertyName === configuratorConstants.PlasteredIndoorWallSides);
                    if (propValue && propValue[0] && propValue[0].value === configuratorConstants.PlasteredIndoorWallSidesNone) {
                            return true;
                    }

                    if (prop.propertyName === configuratorConstants.GroundShape || prop.propertyName === configuratorConstants.OuterCorners
                        || prop.propertyName === configuratorConstants.InnerCorners) {
                            containsGroundShapeOrCorners = configList.includes(configuratorConstants.OuterCorners)
                            || configList.includes(configuratorConstants.GroundShape);
                            return containsGroundShapeOrCorners;
                        }

                    return configList.includes(prop.propertyName);
            })));
    }

    get products() {
        return store.products;
    }

    get translations() {
        return store.translations;
    }

    protected created() {
        this.translationModel = store.translations;
        this.sharedConfiguratorInfoFound = window.location.href.indexOf("shareId") > -1 ? this.$attrs.sharedconfiguratorinfofound.toLowerCase() === "true" : true;
        http.get<Models.ProductConfiguratorModel>(this.apiUrl)
        .then(data => {
             this.vm = data;
             this.selectedConfiguratorProducts += this.vm.configuratorType;
             this.selectedCustomConfiguratorProducts += this.vm.configuratorType;
             this.getCookie();
             store.addPropertyGroups(data.propertyGroups);
             this.initiateConfigurator();
             this.calculateProductQuantity();

             if (this.vm.configuratorType === "BathroomProductSelector") {
                 this.checkMandatoryStepPropertyValuesSet();
             } else if (this.vm.configuratorType === configuratorConstants.StepByStepInstructions) {
                this.isMandatoryStepPropertyValuesSet = true;
             }

             if (!isNaN(data.plasticBoardPercentage) && data.plasticBoardPercentage > 0) {
                this.updateFieldValuesFromSettings(configuratorConstants.PlasticBoardPercentage, data.plasticBoardPercentage);
             }

             if (!isNaN(data.securityMargin) && data.securityMargin > 0) {
                this.updateFieldValuesFromSettings(configuratorConstants.SecurityMargin, data.securityMargin);
             }

             if (data.materialConsumptionFactorOutdoorWall && Number(data.materialConsumptionFactorOutdoorWall.toString().replace(",", ".")) > 0) {
                this.updateFieldValuesFromSettings(configuratorConstants.MaterialConsumptionFactorOutdoorWall, data.materialConsumptionFactorOutdoorWall);
             }

             if (data.materialConsumptionFactorIndoorWall && Number(data.materialConsumptionFactorIndoorWall.toString().replace(",", ".")) > 0) {
                this.updateFieldValuesFromSettings(configuratorConstants.MaterialConsumptionFactorIndoorWall, data.materialConsumptionFactorIndoorWall);
             }

             if (data.materialConsumptionFactorExaktOutdoorWall && Number(data.materialConsumptionFactorExaktOutdoorWall.toString().replace(",", ".")) > 0) {
                this.updateFieldValuesFromSettings(configuratorConstants.MaterialConsumptionFactorExaktOutdoorWall, data.materialConsumptionFactorExaktOutdoorWall);
             }

             if (data.cornerBlockOpeningBlockQuantityFactor && Number(data.cornerBlockOpeningBlockQuantityFactor.toString().replace(",", ".")) > 0) {
                this.updateFieldValuesFromSettings(configuratorConstants.CornerBlockOpeningBlockQuantityFactor, data.cornerBlockOpeningBlockQuantityFactor);
             }

             if (data.adjustmentFactorForNormalBlockPassPicker && Number(data.adjustmentFactorForNormalBlockPassPicker.toString().replace(",", ".")) > 0) {
                this.updateFieldValuesFromSettings(configuratorConstants.AdjustmentFactorForNormalBlockPassPicker, data.adjustmentFactorForNormalBlockPassPicker);
             }
        });
    }

    protected updateFieldValuesFromSettings(name: string, data: any) {
        const updateModel: Models.ConfiguratorValueUpdateModel = { moduleId: "", value: data, valueDescriptions: [{ title: "", text: "" }],
        propertyName: name, isText: false, resetClimateSavingsCookie: false };
        this.updateConfiguratorValue(updateModel);
    }

    protected replaceECOVariants() {
        const ecoVarintCookie = Cookie.getCookie(this.vm.configuratorType + configuratorConstants.ECOProductCookieSuffix);
        const productsData = ecoVarintCookie.split("$");
        productsData.map(product => {
            const articleNumbers = product.split("-");
            this.remapToDifferentVariant(articleNumbers[0], articleNumbers[1], false, Number(articleNumbers[2]));
        });
    }

    protected mounted() {
        EventBus.$on("selectedECOvariant", (carbonSavingsModel: CarbonSavingsModel, quantity: number) => {
            this.remapToDifferentVariant(carbonSavingsModel.currentVariantId, carbonSavingsModel.ecoVariantId, true, carbonSavingsModel.packagingFactor);
        });
    }

    protected remapToDifferentVariant(fromVariantId: string, toVariantId: string, updateCookie: boolean, packagingFactor: number) {
        const fromProduct = this.vm.products.find(x => x.articleNumber === fromVariantId);

        if (fromProduct) {
            http.get<Models.Product>(this.apiUrl + "/getproducttoreplace/" + toVariantId).then(productData => {
                if (productData) {
                    productData.calculatedQuantity = Math.ceil(fromProduct.calculatedQuantity * packagingFactor);
                    productData.confirmedQuantity = Math.ceil(fromProduct.confirmedQuantity * packagingFactor);
                    productData.added = true;
                    if (fromProduct.calculatedQuantity > 0) {
                        store.addProduct(productData);
                        store.removeProduct(fromProduct);

                        if (updateCookie) {
                            Cookie.CreateCarbonSavingsCookie(store.configuratorType + configuratorConstants.ECOProductCookieSuffix, fromVariantId, toVariantId, packagingFactor);
                        }
                    }
                }
            });
        }
    }

    protected initiateConfigurator() {
        store.setConfiguratorType(this.vm.configuratorType);
        this.availableProducts = [...this.vm.products];
        this.vm.steps.forEach(step => step.sections.forEach(section => section.modules.forEach(module => {
                        if (module.moduleType === Models.ProductConfiguratorModuleType.ProductListModule) {
                            const productListModule = module as Models.ProductListModuleModel;
                            store.addProductListModule(productListModule);
                            this.availableProducts.push(...productListModule.products);
                        }

                        if (module.moduleType === Models.ProductConfiguratorModuleType.StepByStepInstructionsProductListModule) {
                            const stepProductListModule = module as Models.ProductListModuleModel;
                            this.availableProducts.push(...stepProductListModule.products);
                        }

                        if (module.configuratorProperties) {
                            module.configuratorProperties.forEach(property =>
                                store.addConfiguratorValue({ id: module.id, value: undefined, valueDescriptions: [], property}));
                        }

                        if (!store.configuratorValues.find(x => x.property.propertyName === configuratorConstants.recommendedProductSelected)) {
                            const recommendedProductSelectedProperty: Models.ConfiguratorProperty = {
                                propertyName: configuratorConstants.recommendedProductSelected,
                                description: configuratorConstants.recommendedProductSelected,
                                mandatory: false,
                                sortIndex: 0,
                            } as Models.ConfiguratorProperty;
                            store.addConfiguratorValue({ id: module.id, value: undefined, valueDescriptions: [], property: recommendedProductSelectedProperty});
                        }
                    }
                )
            )
        );

        if (window.location.href.indexOf("shareId") > -1 && this.sharedConfiguratorInfoFound) {
            this.setActiveStep(this.vm.steps.length - 1);
        }
    }

    protected updateConfiguratorValue(value: Models.ConfiguratorValueUpdateModel, setLocalStorage: boolean = false) {
        store.updateConfiguratorValue(value).then(() => {
            if (value.propertyName === configuratorConstants.GroundShape) {
                EventBus.$emit("updateoutercorners");
            }
            if (value.propertyName === configuratorConstants.OuterCorners || value.propertyName === configuratorConstants.InnerCorners) {
                EventBus.$emit("updategroundshape");
            }
            if (value.propertyName === configuratorConstants.FloorType) {
                EventBus.$emit("onfloortypeupdate");
            }

            if (value.resetClimateSavingsCookie) {
                Cookie.removeCookies([store.configuratorType + configuratorConstants.ECOProductCookieSuffix]);
                this.$forceUpdate();
            }

            this.clearHiddenSectionPropertyValues();
            this.calculateProductQuantity();
            if (setLocalStorage) {
                this.setCookie(value);
            }

            this.checkMandatoryStepPropertyValuesSet();
        });
    }

    protected setCookie(value: any) {
        if ((value.value.wallSections as Models.BuildingWallSection) !== undefined) {
            this.selectedCustomConfiguratorProductCookie = Cookie.setCookie(this.selectedCustomConfiguratorProductCookie, value, this.selectedCustomConfiguratorProducts);
        } else {
            this.selectedConfiguratorProductCookie = Cookie.setCookie(this.selectedConfiguratorProductCookie, value, this.selectedConfiguratorProducts);
        }
    }

    protected getCookie() {
        this.selectedConfiguratorProductCookie = Cookie.getCookie(this.selectedConfiguratorProducts);
        this.selectedCustomConfiguratorProductCookie = Cookie.getCookie(this.selectedCustomConfiguratorProducts);
    }

    protected calculateProductQuantity() {
        if (this.availableProducts.length) {
            const request: Models.CalculateProductQuantityRequestModel = {
                    products: this.availableProducts,
                    configuratorValues: store.configuratorValues
            };

            http.post<Models.CalculateProductQuantityResponseModel>(this.apiUrl + "/calculateproductquantity", request)
            .then(data => {
                const moduleProductsWithoutAutoAdd: Models.CoreProduct[] = [];

                store.productListModules.forEach(productListModule => {
                    const moduleProducts = data.products.filter(p => productListModule.products.map(x => x.articleNumber).includes(p.articleNumber));
                    moduleProducts.forEach(p => {
                            if (!productListModule.autoAddProducts) {
                                moduleProductsWithoutAutoAdd.push(p);
                            }
                            this.handleProductUpdate(p, productListModule.autoAddProducts);
                        });
                });

                const configuratorProducts = data.products.filter(p => this.availableProducts.map(x => x.articleNumber).includes(p.articleNumber));
                configuratorProducts.forEach(p => {
                    let autoAddIndicator = true;
                    if (store.configuratorType === configuratorConstants.BasicElementsProductSelector) {
                        if (moduleProductsWithoutAutoAdd && moduleProductsWithoutAutoAdd.map(x => x.articleNumber).includes(p.articleNumber)) {
                            autoAddIndicator = false;
                        }
                    }

                    if (store.configuratorType === configuratorConstants.BuildingProductSelector) {
                        if (moduleProductsWithoutAutoAdd && !moduleProductsWithoutAutoAdd.map(x => x.articleNumber).includes(p.articleNumber)) {
                            autoAddIndicator = true;
                        }
                    }
                    this.handleProductUpdate(p, autoAddIndicator);
                });

                data.propertyModelValues.forEach(prop => store.addPropertyModelValue(prop));
                this.replaceECOVariants();
                if (data.changeCalculatedPlaster) {
                    const remapFromProducts = this.availableProducts.filter(x => x.changeToPlasterProduct !== "");
                    if (remapFromProducts) {
                        remapFromProducts.map(x => {
                            this.remapToDifferentVariant(x.articleNumber, x.changeToPlasterProduct, true, 1);
                        });
                    }
                }
            })
            .then(() => {
                if (this.displayQuantityChangedMessage) {
                    this.showQuantityChangedModal();
                }
            });
        }
    }

    protected setActiveStep(stepIndex: number) {
        EventBus.$emit("stepchanged");
        this.activeStepIndex = stepIndex;
    }

    protected displayNextStepButton(step: Models.Step): boolean {
        return this.activeStepIndex < this.vm.steps.length && step.displayNextStepButton;
    }

    protected cookieExists(): boolean {
        return this.selectedConfiguratorProductCookie !== "" || this.selectedCustomConfiguratorProductCookie !== "";
    }

    protected getModuleComponent(module: Models.Module) {
        return Models.ProductConfiguratorModuleType[module.moduleType];
    }

    protected getModuleProps(module: Models.Module) {
        return { vm: module };
    }

    protected goToStep(stepIndex: number) {
        this.setActiveStep(stepIndex);
        this.scrollToElement("progress-bar");
        this.checkMandatoryStepPropertyValuesSet();
    }

    protected scrollToElement(elementRef: string) {
        const element = this.$refs[elementRef] as HTMLElement;
        if (element) {
            window.scrollTo(0, element.offsetTop);
        }
    }

    protected hideQuantityChangedModal() {
        this.displayQuantityChangedMessage = false;
        this.$modal.hide(this.quantityChangedModalName);
    }

    protected showQuantityChangedModal() {
        this.$modal.show(this.quantityChangedModalName);
    }

    protected visibleSections(step: Models.Step) {
        return step.sections.filter(section => !section.conditionalPropertyValues.length ||
            section.conditionalPropertyValues
            .every(cpv => store.propertyModelValues
                .some(cv => cv.key === cpv.propertyName && cpv.validPropertyValues.some(value => {
                    if (cv.key === configuratorConstants.recommendedProductSelected && cv.value !== "null") {
                        if (cv.value) {
                            const valueToCheck = cv.value.replace("null", "");
                            if (valueToCheck !== null) {
                                if (value.indexOf("<>") > -1) {
                                    return valueToCheck === "" || valueToCheck.indexOf(value.replace("<>", "") + "$") < 0;
                                } else {
                                   return (value.indexOf(";") > -1 ? ((value.split(";").some(a => a !== "" && valueToCheck.toString().indexOf(a + "$") > -1)) || (value === "*" && valueToCheck))
                                    : (value + "$" === valueToCheck.toString() || valueToCheck.toString().indexOf(value + "$") > -1 || (value === "*" && valueToCheck)));
                                }
                            }
                        } else {
                            if (value.indexOf("<>") > -1) {
                                return true;
                            }
                        }
                    }
                    return cv.value !== null &&
                        (value.indexOf(";") > -1 ? ((value.split(";").some(a => a === cv.value.toString())) || (value === "*" && cv.value))
                        : (value === cv.value.toString() || (value === "*" && cv.value)));
                    })
                )
            )
        );
    }

    private clearHiddenSectionPropertyValues() {
        this.vm.steps.forEach(step => {
            const hiddenSections =  step.sections.filter(section => !this.visibleSections(step).map(x => x.id).includes(section.id));

            hiddenSections.forEach(section =>
                section.modules.forEach(module => {
                    if (module.configuratorProperties) {
                        module.configuratorProperties.filter(a => a.propertyName !== configuratorConstants.PlasticBoardPercentage
                            && a.propertyName !== configuratorConstants.SecurityMargin
                            && !a.settingsProperty)
                        .forEach(p =>
                            store.updateConfiguratorValue({ moduleId: module.id, propertyName: p.propertyName, value: undefined, valueDescriptions: [], isText: false,
                                resetClimateSavingsCookie: false })
                        );
                    }
                })
            );
        });
    }

    private handleProductUpdate(coreProduct: Models.CoreProduct, autoAddProduct: boolean) {
        const storedProduct = this.products.find(p => p.articleNumber === coreProduct.articleNumber);
        const product = this.getProductFromCoreProduct(coreProduct);

        if (product) {
            product.calculatedQuantity = coreProduct.calculatedQuantity;
            product.confirmedQuantity = coreProduct.confirmedQuantity;

            if (configuratorConstants.BuildingConfiguratorautoAddProducts.indexOf(product.articleNumber + ";")  > -1
                && product.calculatedQuantity > 0 && store.configuratorType === configuratorConstants.BuildingProductSelector) {
                    product.added = true;
                }

            if (storedProduct) {
                if (product.calculatedQuantity === 0) {
                    store.removeProduct(product);
                    return;
                }

                product.added =  storedProduct.added;

                if (storedProduct.calculatedQuantity !== product.calculatedQuantity) {
                    if (storedProduct.confirmedQuantity !== storedProduct.calculatedQuantity) {
                        this.displayQuantityChangedMessage = true;
                    }

                    store.addProduct(product);
                }

            } else {
                if (product.calculatedQuantity > 0) {
                    if (autoAddProduct) {
                        product.added = true;
                    }

                    if (store.configuratorType === configuratorConstants.BuildingProductSelector) {
                        if (store.propertyModelValues.filter(x => x.key === configuratorConstants.recommendedProductSelected)[0]) {
                            if (store.propertyModelValues.filter(x => x.key === configuratorConstants.recommendedProductSelected)[0].value &&
                                store.propertyModelValues.filter(x => x.key === configuratorConstants.recommendedProductSelected)[0].value.indexOf(product.articleNumber + "$") > -1) {
                                product.added = true;
                            }
                        }
                    }
                    store.addProduct(product);
                }
            }
        }
    }

    private getProductFromCoreProduct(coreProduct: Models.CoreProduct) {
        return this.availableProducts.find(p => p.articleNumber === coreProduct.articleNumber);
    }

}
